import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Grid, useOverlay, Row, useStatusOverlay, LabelTooltip, H5 } from '@fs/zion-ui'
import { ArrowChange } from '@fs/zion-icon'
import { UserContext } from '../UserContext'
import DataColumnView from '../../common/DataColumnView'
import { resyncCmisx } from '../../../services/membershipService'
import { TreatAsMember } from './TreatAsMember'
import { printValueOrBooleanReadable } from '../../../util/StringUtil'
import ClearContactNameCacheDialog from './ClearContactNameCacheDialog'
import { PermissionsContext } from '../../app/PermissionsContext'
import DmcCopyToClipboardTooltip from '../../../util/DmcCopyToClipboardTooltip'
import CreatePedigreeDialog from './CreatePedigreeDialog'
import { getUserJurisdictions } from '../../../services/userService'
import useNotifications from '../../../hooks/useNotifications'

/* Depending on how wide the screen is, different flex-direction: column heights
 * are needed in <DataColumnView />
 * This array also varies depending on how many tuples are displayed. */
const FIELD_ORDER = { col: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] }
const USERNAME = 'username'
const CONTACT_NAME = 'contactName'
const CIS_ID = 'cisId'
const HELPER_PIN = 'helperPin'
const ENABLED = 'enabled'
const JURISDICTIONS = 'jurisdictions'

const data = []

export default function UserCmis() {
  const { currentUser, fetchCurrentUser } = useContext(UserContext)
  const { isRole } = useContext(PermissionsContext)
  const { dmcUser, metadata, dmcUserMembership } = currentUser
  const [populate, setPopulate] = useState(true)
  const clearContactNameCacheDialogOpener = useOverlay()
  const createPedigreeDialogOpener = useOverlay()
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const createPedigreeButtonDisabled = currentUser?.dmcUser?.enabled === false || !currentUser?.dmcUser?.username
  const [jurisdictions, setJurisdictions] = useState([])
  const { error } = useNotifications()

  useEffect(() => {
    setJurisdictions([])
    if (dmcUser) {
      getUserJurisdictions(dmcUser.cisId)
        .then((jurisdictionsResponse) => {
          setJurisdictions(jurisdictionsResponse.data)
          setPopulate(true)
        })
        .catch((err) => {
          // 404 is ok
          if (err.status !== 404) {
            let message = err.message
            if (err.status === 403) {
              message = "You don't have access to jurisdictions"
            } else
              error(
                t('user.errors.gettingUserJurisdictions', {
                  defaultValue: `Error getting user jurisdictions: ${message || ''}`,
                  message: message || '',
                }),
                err
              )
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dmcUser])

  if (!dmcUser) return null

  let mrns = []
  if (metadata.items && metadata.items.filter((item) => item.field === 'mrn').length > 0) {
    mrns = metadata.items.filter((item) => item.field === 'mrn')[0].values.map((item) => item.value)
  }

  if (mrns.length === 0 && dmcUserMembership?.mrn != null) {
    mrns = [dmcUserMembership.mrn]
  }

  const CMIS_FIELD_NAMES = [
    { idx: 0, name: USERNAME, label: t('user.name', 'Username') },
    { idx: 1, name: CONTACT_NAME, label: t('contactName', 'Contact Name') },
    { idx: 2, name: CIS_ID, label: t('cisId', 'CIS ID') },
    { idx: 3, name: HELPER_PIN, label: t('helperPin', 'Helper PIN') },
    { idx: 4, name: 'profileName', label: t('profileName', 'Profile Name') },
    { idx: 5, name: 'givenName', label: t('givenName', 'Given Name') },
    { idx: 6, name: 'familyName', label: t('familyName', 'Family Name') },
    { idx: 7, name: 'country', label: t('country', 'Country') },
    { idx: 8, name: 'preferredLanguage', label: t('preferredLanguage', 'Preferred Language') },
    { idx: 9, name: 'sms', label: t('cmis.sms', 'SMS') },
    { idx: 10, name: 'email', label: t('email', 'Email') },
    { idx: 11, name: ENABLED, label: t('account.status', 'Account Status') },
    { idx: 12, name: JURISDICTIONS, label: t('jurisdictions', 'Jurisdictions') },
  ]

  const populateView = () => {
    const keys = Object.keys(FIELD_ORDER)
    const size = keys.length
    let resultArrayIndex = 0
    keys.forEach((key) => {
      let i = resultArrayIndex
      FIELD_ORDER[key].forEach((item) => {
        const field = CMIS_FIELD_NAMES.filter((e) => e.idx === item)
        switch (field[0].name) {
          case USERNAME:
          case CONTACT_NAME:
          case CIS_ID:
          case HELPER_PIN:
            data[i] = {
              label: field[0].label,
              detail: dmcUser[field[0].name] ? (
                <DmcCopyToClipboardTooltip value={dmcUser[field[0].name]} nextToText />
              ) : null,
            }
            break
          case ENABLED: {
            let detail = 'Unknown'
            if (dmcUser[field[0].name] === true) {
              detail = 'Enabled'
            } else if (dmcUser[field[0].name] === false) {
              detail = 'Disabled'
            }
            data[i] = {
              label: field[0].label,
              detail,
            }
            break
          }
          case JURISDICTIONS: {
            data[i] = {
              label: field[0].label,
              detail: jurisdictions ? jurisdictions.map((jurisdiction) => jurisdiction.jurisdiction).join(', ') : 'Hmm',
            }
            break
          }
          default: {
            const booleanValue = printValueOrBooleanReadable(dmcUser[field[0].name])
            data[i] = {
              label: field[0].label,
              detail: booleanValue !== null ? booleanValue : dmcUser[field[0].name],
            }
          }
        }
        i += size
      })
      resultArrayIndex += 1
    })

    setPopulate(false)
  }

  if (populate) populateView()

  const handleResync = async () => {
    if (mrns.length > 0) {
      const promises = mrns.map((mrn) => resyncCmisx(mrn))
      await Promise.all(promises)
      fetchCurrentUser()
    } else {
      showStatusOverlay({ message: t('user.overlay.noMrns', 'No Mrns Available') })
    }
  }

  return (
    <Grid>
      <Row wrap alignY="middle">
        <H5 growX>{t('user.summary', 'User Summary')}</H5>

        <Row wrap alignY="middle">
          {isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && (
            <>
              <LabelTooltip
                label={
                  createPedigreeButtonDisabled
                    ? t(
                        'cmis.createPedigree.disabledMessage',
                        'Not available due to no current account or account status.'
                      )
                    : ''
                }
                target={
                  <Button
                    disabled={createPedigreeButtonDisabled}
                    emphasis="medium"
                    onClick={createPedigreeDialogOpener.handleClick}
                    description={t('user.buttons.createPedigree.description', 'Create Pedigree')}
                  >
                    {t('user.buttons.createPedigree.label', 'Create Pedigree')}
                  </Button>
                }
              />
              <CreatePedigreeDialog currentUser={currentUser} {...createPedigreeDialogOpener} />
            </>
          )}
          <Button variant="contained" emphasis="medium" onClick={handleResync}>
            {t('cmis.resync', 'Resync CMISx')}
          </Button>
          {isRole('Data Admin 6') && currentUser.dmcUser.cisId && (
            <>
              <Button
                Icon={ArrowChange}
                emphasis="medium"
                onClick={clearContactNameCacheDialogOpener.handleClick}
                description={t('user.buttons.clearContactNameCache.description', 'Clear Contact Name Cache')}
              >
                {t('user.buttons.clearContactNameCache.label', 'Clear Cache')}
              </Button>
              <ClearContactNameCacheDialog {...clearContactNameCacheDialogOpener} />
            </>
          )}
        </Row>
      </Row>
      <DataColumnView data={data} />
      <TreatAsMember cisId={dmcUser.cisId} />
    </Grid>
  )
}
